import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  PersonOutline as UserIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import apiClient from '@/api/client';

const UserAuditLog = () => {
  const [userAudit, setUserAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Filter states
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  
  // Pagination state - DataGrid will handle this client-side
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // Fetch ALL user audit logs using the centralized API client
  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      // apiClient already has the base URL configured and token interceptor
      const response = await apiClient.get("/user/audit-logs?per_page=10000");
      
      // Handle paginated response (same as before)
      const logs = Array.isArray(response.data.data) 
        ? response.data.data 
        : Array.isArray(response.data) 
        ? response.data 
        : [];
      setUserAudit(logs);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      
      // Handle 401 Unauthorized (token expired / invalid)
      if (error.response?.status === 401) {
        Swal.fire({
          icon: "error",
          title: "Unauthorized!",
          text: "Please login again to continue.",
          confirmButtonText: "OK",
        }).then(() => {
          localStorage.removeItem("token");
          window.location.href = "/login";
        });
        return;
      }
      
      // Show generic error
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error.message || "Failed to fetch audit logs. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  // Get unique action types for filter (like Admin Audit Log)
  const actionTypes = useMemo(() => {
    const types = new Set(userAudit.map(log => log.action_type));
    return Array.from(types);
  }, [userAudit]);

  // Get unique roles for filter (like Admin Audit Log)
  const roles = useMemo(() => {
    const uniqueRoles = new Set(userAudit.map(log => log.user_role));
    return Array.from(uniqueRoles);
  }, [userAudit]);

  const getActionChip = (actionType) => {
    const colors = {
      form_submission: 'success',
      form_update: 'info',
      form_deletion: 'error',
      status_update: 'warning',
      user_login: 'primary',
      user_logout: 'secondary',
      permission_change: 'warning',
    };
    
    return (
      <Chip 
        label={actionType?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
        color={colors[actionType] || 'default'}
        size="small"
        sx={{ fontWeight: 500, minWidth: 100 }}
      />
    );
  };

  const getRoleChip = (role) => {
    const isAdmin = role === 'ADMIN' || role === 'admin';
    return (
      <Chip 
        label={role}
        size="small"
        color={isAdmin ? 'error' : 'primary'}
        variant="outlined"
        icon={isAdmin ? <AdminIcon /> : <UserIcon />}
      />
    );
  };

  // Apply filters - client-side filtering (like Admin Audit Log)
  const filteredRows = useMemo(() => {
    let filtered = userAudit;

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(log => 
        log.user_name?.toLowerCase().includes(searchLower) ||
        log.user_email?.toLowerCase().includes(searchLower) ||
        log.action_type?.toLowerCase().includes(searchLower) ||
        log.entity_area?.toLowerCase().includes(searchLower) ||
        log.description?.toLowerCase().includes(searchLower) ||
        log.ip_address?.toLowerCase().includes(searchLower)
      );
    }

    // Apply role filter
    if (roleFilter) {
      filtered = filtered.filter(log => log.user_role === roleFilter);
    }

    // Apply action type filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action_type === actionFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(thisWeek.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(thisMonth.getMonth() - 1);

      filtered = filtered.filter(log => {
        const logDate = new Date(log.action_date_time);
        switch(dateFilter) {
          case 'today':
            return logDate >= today;
          case 'yesterday':
            return logDate >= yesterday && logDate < today;
          case 'thisWeek':
            return logDate >= thisWeek;
          case 'thisMonth':
            return logDate >= thisMonth;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [userAudit, searchText, roleFilter, actionFilter, dateFilter]);

  const clearFilters = () => {
    setSearchText("");
    setRoleFilter("");
    setActionFilter("all");
    setDateFilter("all");
  };

  const renderNewValuesUI = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary">No form data available.</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {Object.entries(data).map(([key, value]) => (
          <Grid item xs={12} sm={6} key={key}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'background.default',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <Typography 
                variant="caption" 
                color="primary" 
                sx={{ 
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}
              >
                {key.replace(/_/g, " ").toUpperCase()}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  wordBreak: "break-word",
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70,
      sortable: true,
    },
    { 
      field: 'user_name', 
      headerName: 'User', 
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.user_email}
            </Typography>
          </Box>
        </Box>
      )
    },
    { 
      field: 'user_role', 
      headerName: 'Role', 
      width: 120,
      sortable: true,
      renderCell: (params) => getRoleChip(params.value)
    },
    { 
      field: 'action_type', 
      headerName: 'Action', 
      width: 150,
      sortable: true,
      renderCell: (params) => getActionChip(params.value)
    },
    { 
      field: 'action_date_time', 
      headerName: 'Date & Time', 
      width: 200,
      sortable: true,
      valueGetter: (params) => new Date(params.value),
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    },
    { 
      field: 'entity_area', 
      headerName: 'Entity', 
      width: 200,
      sortable: true,
      renderCell: (params) => (
        <Chip 
          label={params.value?.replace(/_/g, ' ') || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ 
            bgcolor: '#f0f0f0',
            fontWeight: 500
          }}
        />
      )
    },
    { 
      field: 'ip_address', 
      headerName: 'IP Address', 
      width: 130,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 350,
      sortable: true,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.4,
            py: 1
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Details',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton 
            size="small" 
            onClick={() => setSelectedLog(params.row)}
            color="primary"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <SecurityIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="600" gutterBottom>
                  User Audit Log
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track all user activities and system interactions
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAuditLogs}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by user, email, action, entity..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <Button 
                      size="small" 
                      onClick={() => setSearchText('')}
                      sx={{ minWidth: 'auto' }}
                    >
                      <ClearIcon fontSize="small" />
                    </Button>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>User Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="User Role"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Action Type</InputLabel>
              <Select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                label="Action Type"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {actionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type?.replace(/_/g, ' ').toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                label="Date Range"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              startIcon={<FilterIcon />}
              sx={{ borderRadius: 2 }}
              disabled={!searchText && !roleFilter && actionFilter === 'all' && dateFilter === 'all'}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="primary.main" fontWeight="600">
              {userAudit.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Actions
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="success.main" fontWeight="600">
              {actionTypes.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Action Types
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="info.main" fontWeight="600">
              {filteredRows.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filtered Results
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h4" color="warning.main" fontWeight="600">
              {roles.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              User Roles
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* DataGrid - Client-side pagination and sorting */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryIcon color="action" />
              <Typography variant="h6" fontWeight="600">
                Activity Log Entries
              </Typography>
            </Stack>
            {searchText || roleFilter || actionFilter !== 'all' || dateFilter !== 'all' ? (
              <Chip 
                label={`${filteredRows.length} results found`}
                color="info"
                size="small"
                onDelete={clearFilters}
              />
            ) : (
              <Chip 
                label={`${userAudit.length} total entries`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          <Divider />
          
          <Box sx={{ height: 'calc(100vh - 500px)', width: '100%', mt: 2 }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                sorting: { 
                  sortModel: [{ field: 'action_date_time', sort: 'desc' }] 
                },
              }}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell': {
                  py: 1,
                  borderRight: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: 'action.hover',
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '2px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 600,
                },
                border: 'none',
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoIcon color="primary" />
            <Typography variant="h6">Form Submission Details</Typography>
          </Stack>
          <IconButton onClick={() => setSelectedLog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Submitted by
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {selectedLog?.user_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedLog?.user_email} • {getRoleChip(selectedLog?.user_role)}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Submitted Data
            </Typography>
            {renderNewValuesUI(selectedLog?.new_values)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setSelectedLog(null)} 
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {loading && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
    </Box>
  );
};

export default UserAuditLog;